import { Command } from 'commander';
import { RedmineConfigService } from '../../services/redmine-config.service.js';
import { syncProject } from '../../sync-project.js';

interface SyncProjectOptions {
    status?: string;
    updatedSince?: string;
    concurrency?: string;
    pageSize?: string;
    dryRun?: boolean;
    config?: string;
    outputDir?: string;
    json?: boolean;
}

/**
 * Create redmine sync project command
 */
export function createSyncProjectCommand(): Command {
    const cmd = new Command('project')
        .description('Sync all issues in a project')
        .option('-s, --status <status>', 'Filter by status (default: *)', '*')
        .option('--updated-since <date>', 'Only sync issues updated since YYYY-MM-DD')
        .option('--concurrency <number>', 'Number of concurrent requests')
        .option('--page-size <number>', 'Page size for API requests')
        .option('--dry-run', 'Preview without making changes')
        .option('-c, --config <path>', 'Config file path')
        .option('-o, --output-dir <path>', 'Output directory')
        .option('--json', 'Output as JSON')
        .action(async (options: SyncProjectOptions) => {
            await handleSyncProject(options);
        });

    return cmd;
}

async function handleSyncProject(options: SyncProjectOptions): Promise<void> {
    const configService = new RedmineConfigService(options.config);
    const config = await configService.load();

    const syncOptions: Parameters<typeof syncProject>[1] = {
        status: options.status,
        updatedSince: options.updatedSince,
        dryRun: options.dryRun,
        outputDir: options.outputDir,
        ...(options.concurrency && { concurrency: parseInt(options.concurrency, 10) }),
        ...(options.pageSize && { pageSize: parseInt(options.pageSize, 10) }),
        onProgress: options.json
            ? undefined
            : (current: number, total: number) => {
                process.stdout.write(`\rProgress: ${current}/${total} issues`);
            },
    };

    const result = await syncProject(config, syncOptions);

    if (!options.json) {
        process.stdout.write('\r'); // Clear progress line
    }

    if (options.json) {
        console.log(JSON.stringify(result, null, 2));
    } else {
        if (result.success) {
            console.log('✅ Sync completed successfully');
            console.log(`   Total issues: ${result.totalIssues}`);
            console.log(`   Created: ${result.created}`);
            console.log(`   Updated: ${result.updated}`);
            console.log(`   Skipped: ${result.skipped}`);
            if (result.failed > 0) {
                console.log(`   Failed: ${result.failed}`);
            }
        } else {
            console.log('❌ Sync completed with errors');
            console.log(`   Total issues: ${result.totalIssues}`);
            console.log(`   Processed: ${result.processed}`);
            console.log(`   Created: ${result.created}`);
            console.log(`   Updated: ${result.updated}`);
            console.log(`   Skipped: ${result.skipped}`);
            console.log(`   Failed: ${result.failed}`);

            if (result.errors.length > 0) {
                console.log('\nErrors:');
                result.errors.forEach((error) => {
                    console.log(`   Issue ${error.issueId}: ${error.error}`);
                });
            }

            process.exit(5);
        }
    }
}
